import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { LocalStorageService } from "./local-storage.service";
import { HttpRequestsService } from "./http-requests.service";
import { ApiFoodService } from "./merchant/food.service.api";
import { tap } from "rxjs/operators";

export interface Restaurant {
  id: string;
  name: string;
  createdAt: string; // Add timestamp field (ISO string format)
}

@Injectable({
  providedIn: "root",
})
export class RestaurantSelectionService {
  private selectedRestaurantSubject = new BehaviorSubject<Restaurant | null>(
    null,
  );
  private restaurantsSubject = new BehaviorSubject<Restaurant[]>([]);

  public selectedRestaurant$ = this.selectedRestaurantSubject.asObservable();
  public restaurants$ = this.restaurantsSubject.asObservable();

  private restaurantChangedSubject = new Subject<Restaurant>();
  public restaurantChanged$ = this.restaurantChangedSubject.asObservable();

  constructor(
    private localStorage: LocalStorageService,
    private httpService: HttpRequestsService,
    private foodService: ApiFoodService,
  ) {
    this.loadSelectedRestaurant();
  }

  private loadSelectedRestaurant(): void {
    const savedRestaurant = this.localStorage.getLocalStore(
      "SELECTED_RESTAURANT",
    );
    if (savedRestaurant) {
      this.selectedRestaurantSubject.next(JSON.parse(savedRestaurant));
    }
  }

  async getRestaurants(): Promise<Restaurant[]> {
    return this.foodService
      .getRestaurants()
      .pipe(
        tap((restaurants) => {
          if (restaurants) {
            this.restaurantsSubject.next(restaurants);

            const currentSelection = this.selectedRestaurantSubject.value;
            if (!currentSelection && restaurants.length > 0) {
              // Auto-select first restaurant if none is selected
              this.selectRestaurant(restaurants[0]);
            } else if (currentSelection) {
              // If there's already a selected restaurant, emit the change event
              // to notify components that restaurants are now loaded
              this.selectRestaurant(currentSelection);
              this.restaurantChangedSubject.next(currentSelection);
            }
          }
        }),
      )
      .toPromise();
  }

  selectRestaurant(restaurant: Restaurant): void {
    const previousRestaurant = this.selectedRestaurantSubject.value;
    // Update HTTP service to include restaurant header in all requests
    this.updateHttpHeaders(restaurant.id);

    this.selectedRestaurantSubject.next(restaurant);
    this.localStorage.setLocalStore(
      "SELECTED_RESTAURANT",
      JSON.stringify(restaurant),
    );

    // Emit restaurant change event only if it's actually different
    if (!previousRestaurant || previousRestaurant.id !== restaurant.id) {
      this.restaurantChangedSubject.next(restaurant);
    }
  }

  private updateHttpHeaders(restaurantId: string): void {
    this.localStorage.setLocalStore("CURRENT_RESTAURANT_ID", restaurantId);
  }

  getCurrentRestaurantId(): string | null {
    return this.localStorage.getLocalStore("CURRENT_RESTAURANT_ID");
  }

  getSelectedRestaurant(): Restaurant | null {
    return this.selectedRestaurantSubject.value;
  }

  hasMultipleRestaurants(): boolean {
    return this.restaurantsSubject.value.length >= 1;
  }

  selectMostRecentRestaurant(): void {
    const restaurants = this.restaurantsSubject.value;

    if (restaurants.length === 0) {
      console.warn("No restaurants available to select");
      return;
    }

    // Find restaurant with most recent createdAt timestamp
    const mostRecentRestaurant = restaurants.reduce((latest, current) => {
      const latestDate = new Date(latest.createdAt);
      const currentDate = new Date(current.createdAt);

      return currentDate > latestDate ? current : latest;
    });

    this.selectRestaurant(mostRecentRestaurant);
  }

  clearRestaurantData(): void {
    this.selectedRestaurantSubject.next(null);
    this.restaurantsSubject.next([]);
  }
}
